import IframeView from 'epubjs/src/managers/views/iframe';
import { EVENTS } from 'epubjs/src/utils/constants';
import { borders } from 'epubjs/src/utils/core';
import { EpubCFI } from 'epubjs';

class HeadingBasedIframeView extends IframeView {
  constructor(section, options) {
    super(section, options);

    this.on('resized', () => {
      if (!this.element) return;

      if (this._wakeLockedHeight !== undefined && !this.displayed) {
        // Waking from sleep: expand() has already written an intermediate height (e.g. 0px or 66px).
        // Restore the locked floor so the container never collapses below what the user last saw,
        // preventing the viewport from jumping while the iframe re-renders above it.
        const currentH = parseFloat(this.element.style.height) || 0;
        if (currentH < this._wakeLockedHeight) {
          this.element.style.height = `${this._wakeLockedHeight}px`;
        }
        return;
      }

      if (this.displayed) {
        // iframe is fully rendered — release the skeleton lock and let height go natural.
        this.element.style.height = '';
        this.element.style.width = '';
        this._wakeLockedHeight = undefined;
      }
    });
  }

  show() {
    this.element.style.visibility = 'visible';

    if (this.iframe) {
      /**
       * Here was strange code, that should fix some issue with the safari.
       * But in reality it does some DOM manipulation that didn't do anything, except adding of more load for some
       * calculations.
       * Original code was containing following:
       *
       *      // Remind Safari to redraw the iframe
       *      this.iframe.style.transform = "translateZ(0)";
       *      this.iframe.offsetWidth;
       *      this.iframe.style.transform = null;
       *
       * Please pay attention, that this code is triggered on each! page that is currently displayed on any!
       * user action. So even small calculation are multiplied by number of views displayed.
       * For me book player started to work much better in safari without this code.
       */
      this.iframe.style.visibility = 'visible';

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        this.iframe.style.willChange = 'transform';
        this.iframe.style.transform = 'translateZ(0)';
      }
    }

    this.emit(EVENTS.VIEWS.SHOWN, this);
  }

  // VIRTUAL SCROLL UNLOADER ---
  unload() {
    if (!this.displayed) return;

    // Lock the container dimensions so the scrollbar doesn't move a single pixel
    const bounds = this.element.getBoundingClientRect();
    this._wakeLockedHeight = bounds.height;
    this.element.style.height = `${bounds.height}px`;
    this.element.style.width = `${bounds.width}px`;

    // Destroy the pane (SVG annotations layer) - same lifecycle as iframe
    // When view wakes up, both pane and iframe will be recreated fresh
    if (this.pane && this.pane.element && this.pane.element.parentNode) {
      this.pane.element.parentNode.removeChild(this.pane.element);
    }
    this.pane = null;

    // Destroy the heavy iframe, freeing up memory
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }

    // Drop the Contents wrapper along with the iframe it describes.
    // Left dangling, it points at a detached document, and epub.js's setLayout()
    // walks every view (sleeping ones included) guarding only on `this.contents`.
    // Firefox then returns null from getComputedStyle() on the detached frame and throws.
    // A fresh Contents is built by load() when the view wakes up.
    if (this.contents) {
      this.contents.destroy();
      this.contents = null;
    }

    // Clean up internal references so epub.js knows it needs to be rebuilt if scrolled to
    this.iframe = null;
    this.displayed = false;
  }

  expand(force) {
    if (this.lockedWidth / this.layout.width !== 1 && this.iframe) {
      const elBorders = borders(this.element);
      const iframeBorders = borders(this.iframe);
      /**
       * Fix views with "locked" width on resize.
       * So re-calculate it's width if layout width was changed.
       */
      this.lockedWidth = this.layout.width - elBorders.width - iframeBorders.width;
    }

    return super.expand(force);
  }

  /**
   * Intercepts CFI resolution failures and falls back to standard HTML IDs
   */
  locationOf(target) {
    // Make sure the document actually exists before searching
    if (!this.document) return { left: 0, top: 0 };

    // If it's a CFI, extract the ID
    if (new EpubCFI().isCfiString(target)) {
      // Decode the string into a structured JavaScript object
      const parsedCfi = new EpubCFI(target);

      // Extract the DOM path steps natively
      const steps = parsedCfi.path ? parsedCfi.path.steps : [];

      // Find the deepest DOM node in the path that contains an ID assertion
      // (We reverse the array to look from the end of the path backwards)
      const stepWithId = steps
        .slice()
        .reverse()
        .find((step) => step.id);
      const elementId = stepWithId ? stepWithId.id : null;

      if (elementId) {
        // Query the live, rendered iframe directly
        const el = this.document.getElementById(elementId);

        if (el) {
          const rect = el.getBoundingClientRect();
          const top = rect.top + (this.iframe.contentWindow?.scrollY || 0);
          const left = rect.left + (this.iframe.contentWindow?.scrollX || 0);

          // Log the actual text content of the element so you can PROVE it found the right thing!
          const textPreview = el.textContent ? el.textContent.substring(0, 40).trim() : 'Empty/Image';
          console.debug(`Found <${el.tagName}> "#${elementId}" | Text: "${textPreview}..." `);

          return { left, top };
        }
      }

      // Handle the fake fallback path gracefully without regex
      if (!elementId && steps.length > 0 && steps[0].step === 1) {
        return { left: 0, top: 0 };
      }
    }

    // Standard fallback
    return super.locationOf(target);
  }

  highlight(cfiRange, data, cb, className, styles) {
    // Validate the range BEFORE creating the highlight
    // Prevents crashes when epub.js tries to inject annotations into not-yet-loaded views
    if (!this.contents) {
      return undefined;
    }

    try {
      const range = this.contents.range(cfiRange);

      if (!range || !range.startContainer || !range.endContainer) {
        // Range invalid - skip silently (view not ready yet)
        return undefined;
      }

      // Validate containers are still in the document
      if (!range.startContainer.isConnected || !range.endContainer.isConnected) {
        // DOM removed - skip silently
        return undefined;
      }
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (e) {
      // Failed to create range - skip silently (expected during navigation)
      return undefined;
    }

    // Range is valid - call parent implementation
    return super.highlight(cfiRange, data, cb, className, styles);
  }
}

export default HeadingBasedIframeView;
